import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface DealExitTabProps {
  kpis: any;
  onKpisChange: (data: any) => void;
}

export function DealExitTab({ kpis, onKpisChange }: DealExitTabProps) {
  const handleExitChange = (field: string, value: any) => {
    const updatedKpis = {
      ...kpis,
      exit: {
        ...(kpis.exit || {}),
        [field]: value,
      },
    };
    onKpisChange(updatedKpis);
  };

  const handleRefinanceChange = (field: string, value: any) => {
    const updatedKpis = {
      ...kpis,
      exit: {
        ...(kpis.exit || {}),
        refinance: {
          ...(kpis.exit?.refinance || {}),
          [field]: value,
        },
      },
    };
    onKpisChange(updatedKpis);
  };

  const handleStrategyChange = (value: string) => {
    const updatedKpis = {
      ...kpis,
      exit: {
        ...(kpis.exit || {}),
        strategy: value,
      },
    };
    onKpisChange(updatedKpis);
  };

  const handleValuationMethodChange = (value: string) => {
    const updatedKpis = {
      ...kpis,
      exit: {
        ...(kpis.exit || {}),
        valuationMethod: value,
        sale: {
          ...(kpis.exit?.sale || {}),
          valuationMethod: value,
        },
      },
    };
    onKpisChange(updatedKpis);
  };

  const handleHoldPeriodChange = (value: number) => {
    const updatedKpis = {
      ...kpis,
      exit: {
        ...(kpis.exit || {}),
        holdPeriodYears: value,
        sale: {
          ...(kpis.exit?.sale || {}),
          exitYear: value,
        },
      },
    };
    onKpisChange(updatedKpis);
  };

  const handleExitCapRateChange = (value: number) => {
    const updatedKpis = {
      ...kpis,
      exit: {
        ...(kpis.exit || {}),
        exitCapRate: value,
        sale: {
          ...(kpis.exit?.sale || {}),
          exitCapRate: value,
        },
      },
    };
    onKpisChange(updatedKpis);
  };

  const handleSellingCostsChange = (value: number) => {
    const updatedKpis = {
      ...kpis,
      exit: {
        ...(kpis.exit || {}),
        sellingCostsPct: value,
        sale: {
          ...(kpis.exit?.sale || {}),
          sellingCostsPct: value,
        },
      },
    };
    onKpisChange(updatedKpis);
  };

  return (
    <TabsContent value="exit" className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Exit Strategy</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <Label className="font-medium">Exit Strategy</Label>
            <Select
              value={kpis.exit?.strategy || "SALE"}
              onValueChange={handleStrategyChange}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SALE">Sale</SelectItem>
                <SelectItem value="REFINANCE">Refinance</SelectItem>
                <SelectItem value="HOLD">Hold</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(!kpis.exit?.strategy || kpis.exit.strategy === "SALE") && (
            <>
              <div className="space-y-3">
                <Label className="font-medium">Hold Period (Years)</Label>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    className="w-24"
                    value={kpis.exit?.holdPeriodYears || 5}
                    onChange={(e) =>
                      handleHoldPeriodChange(Number(e.target.value) || 5)
                    }
                  />
                  <div className="flex flex-wrap gap-2">
                    {[3, 5, 7, 10, 15, 20].map((v) => (
                      <Button
                        key={v}
                        type="button"
                        variant={
                          kpis.exit?.holdPeriodYears === v
                            ? "default"
                            : "outline"
                        }
                        size="sm"
                        className={`h-8 px-3 text-sm ${
                          kpis.exit?.holdPeriodYears === v
                            ? "bg-[hsl(215,85%,45%)] hover:bg-[hsl(215,85%,40%)] text-white"
                            : ""
                        }`}
                        onClick={() => handleHoldPeriodChange(v)}
                      >
                        {v}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label className="font-medium">Exit Cap Rate (%)</Label>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    step="0.5"
                    className="w-24"
                    value={kpis.exit?.exitCapRate || 7}
                    onChange={(e) =>
                      handleExitCapRateChange(Number(e.target.value) || 7)
                    }
                  />
                  <div className="flex flex-wrap gap-2">
                    {[5, 5.5, 6, 6.5, 7, 7.5, 8].map((v) => (
                      <Button
                        key={v}
                        type="button"
                        variant={
                          kpis.exit?.exitCapRate === v ? "default" : "outline"
                        }
                        size="sm"
                        className={`h-8 px-3 text-sm ${
                          kpis.exit?.exitCapRate === v
                            ? "bg-[hsl(215,85%,45%)] hover:bg-[hsl(215,85%,40%)] text-white"
                            : ""
                        }`}
                        onClick={() => handleExitCapRateChange(v)}
                      >
                        {v}%
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label className="font-medium">Selling Costs (%)</Label>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    step="0.5"
                    className="w-24"
                    value={kpis.exit?.sellingCostsPct || 2}
                    onChange={(e) =>
                      handleSellingCostsChange(Number(e.target.value) || 2)
                    }
                  />
                  <div className="flex flex-wrap gap-2">
                    {[1, 1.5, 2, 2.5, 3].map((v) => (
                      <Button
                        key={v}
                        type="button"
                        variant={
                          kpis.exit?.sellingCostsPct === v
                            ? "default"
                            : "outline"
                        }
                        size="sm"
                        className={`h-8 px-3 text-sm ${
                          kpis.exit?.sellingCostsPct === v
                            ? "bg-[hsl(215,85%,45%)] hover:bg-[hsl(215,85%,40%)] text-white"
                            : ""
                        }`}
                        onClick={() => handleSellingCostsChange(v)}
                      >
                        {v}%
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label className="font-medium">Valuation Method</Label>
                <Select
                  value={kpis.exit?.valuationMethod || "capRate"}
                  onValueChange={handleValuationMethodChange}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="capRate">Cap Rate</SelectItem>
                    <SelectItem value="multiple">NOI Multiple</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {kpis.exit?.valuationMethod === "multiple" && (
                <div className="space-y-3">
                  <Label className="font-medium">Exit NOI Multiple</Label>
                  <Input
                    type="number"
                    step="0.1"
                    className="w-24"
                    value={kpis.exit?.exitNOIMultiple || 12}
                    onChange={(e) =>
                      handleExitChange(
                        "exitNOIMultiple",
                        Number(e.target.value) || 12,
                      )
                    }
                  />
                </div>
              )}
            </>
          )}

          {kpis.exit?.strategy === "REFINANCE" && (
            <>
              <div className="space-y-3">
                <Label className="font-medium">Refinance Year</Label>
                <Input
                  type="number"
                  className="w-24"
                  value={kpis.exit?.refinance?.refinanceYear || 5}
                  onChange={(e) =>
                    handleRefinanceChange(
                      "refinanceYear",
                      Number(e.target.value) || 5,
                    )
                  }
                />
              </div>

              <div className="space-y-3">
                <Label className="font-medium">LTV at Refinance (%)</Label>
                <Input
                  type="number"
                  className="w-24"
                  value={kpis.exit?.refinance?.ltvAtRefinance || 70}
                  onChange={(e) =>
                    handleRefinanceChange(
                      "ltvAtRefinance",
                      Number(e.target.value) || 70,
                    )
                  }
                />
              </div>

              <div className="space-y-3">
                <Label className="font-medium">Refinance Costs (%)</Label>
                <Input
                  type="number"
                  className="w-24"
                  value={kpis.exit?.refinance?.refinanceCostsPct || 2}
                  onChange={(e) =>
                    handleRefinanceChange(
                      "refinanceCostsPct",
                      Number(e.target.value) || 2,
                    )
                  }
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {(!kpis.exit?.strategy || kpis.exit.strategy === "SALE") && (
        <Card>
          <CardHeader>
            <CardTitle>Exit Valuation Estimate</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Estimated NOI at Exit:</span>
                <span className="font-medium">
                  {kpis.currency || "EUR"}
                  {(0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Exit Cap Rate:</span>
                <span className="font-medium">
                  {kpis.exit?.exitCapRate || 7}%
                </span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span>Exit Value:</span>
                <span className="font-bold">
                  {kpis.currency || "EUR"}
                  {(0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Selling Costs ({kpis.exit?.sellingCostsPct || 2}%):</span>
                <span className="font-medium">
                  {kpis.currency || "EUR"} {(0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span>Net Proceeds:</span>
                <span className="font-bold text-lg">
                  {kpis.currency || "EUR"} {(0).toLocaleString()}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="p-4 bg-muted/30 rounded-lg border">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <div>
            <p className="font-medium text-sm">Exit Valuation Note</p>
            <p className="text-sm text-muted-foreground mt-1">
              Exit valuation and IRR calculations will be computed based on
              projected NOI at exit year and cap rate assumptions.
            </p>
            {kpis.exit?.strategy === "REFINANCE" && (
              <p className="text-sm text-muted-foreground mt-1">
                For refinance strategy, calculations assume loan restructuring
                based on property value at refinance year.
              </p>
            )}
          </div>
        </div>
      </div>
    </TabsContent>
  );
}
